import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  deleteOneOffStop,
  deleteRouteAssignment,
  type RouteAssignment,
} from '@/features/service-locations/api/serviceLocations';

interface Props {
  clientId: number;
  locationId: number;
  assignments: RouteAssignment[];
  oneOff: RouteAssignment[];
  upcoming: string[];
  onChanged: () => Promise<unknown>;
}

export function ScheduleManager({
  clientId,
  locationId,
  assignments,
  oneOff,
  upcoming,
  onChanged,
}: Props) {
  const navigate = useNavigate();
  const [selectedAssignments, setSelectedAssignments] = useState<Set<number>>(new Set());
  const [selectedOneOff, setSelectedOneOff] = useState<Set<number>>(new Set());
  const remove = useMutation({
    mutationFn: async ({ ids, kind }: { ids: number[]; kind: 'recurring' | 'one-off' }) => {
      await Promise.all(
        ids.map((id) =>
          kind === 'recurring'
            ? deleteRouteAssignment(clientId, locationId, id)
            : deleteOneOffStop(clientId, locationId, id),
        ),
      );
    },
    onSuccess: async (_, variables) => {
      if (variables.kind === 'recurring') setSelectedAssignments(new Set());
      else setSelectedOneOff(new Set());
      await onChanged();
    },
  });
  const root = `/clients/${clientId}/service-locations/${locationId}`;

  return (
    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
      {remove.isError && (
        <Alert severity="error">The selected schedule records could not be deleted.</Alert>
      )}
      <ScheduleSubgrid
        title="Recurring route assignments"
        ariaLabel="Recurring route assignments"
        records={assignments}
        selectedIds={selectedAssignments}
        onSelectionChange={setSelectedAssignments}
        columns={['Technician', 'Frequency', 'Start', 'End']}
        cells={(item) => [
          item.user?.name ?? `Technician ${item.user_id}`,
          item.frequency,
          date(item.start_date),
          item.end_date ? date(item.end_date) : 'Ongoing',
        ]}
        onNew={() => void navigate(`${root}/route-assignments/new`)}
        onOpen={(id) => void navigate(`${root}/route-assignments/${id}`)}
        onDelete={(ids) => {
          const assignmentLabel =
            ids.length === 1 ? 'this recurring assignment' : `these ${ids.length} recurring assignments`;
          if (
            window.confirm(
              `Delete ${assignmentLabel}? The service location, pool details, reports, one-off visits, and other recurring assignments will remain.`,
            )
          ) {
            remove.mutate({ ids, kind: 'recurring' });
          }
        }}
        deleteLabel="Delete assignment"
        deleting={remove.isPending}
      />
      <ScheduleSubgrid
        title="One-off visits"
        ariaLabel="One-off visits"
        records={oneOff}
        selectedIds={selectedOneOff}
        onSelectionChange={setSelectedOneOff}
        columns={['Technician', 'Visit date', 'Notes']}
        cells={(item) => [
          item.user?.name ?? `Technician ${item.user_id}`,
          date(item.start_date),
          item.notes?.trim() || '—',
        ]}
        onNew={() => void navigate(`${root}/one-off-visits/new`)}
        onOpen={(id) => void navigate(`${root}/one-off-visits/${id}`)}
        onDelete={(ids) => {
          const visitLabel =
            ids.length === 1 ? 'this one-off visit' : `these ${ids.length} one-off visits`;
          if (
            window.confirm(
              `Delete ${visitLabel}? The service location, pool details, reports, recurring schedule, and other one-off visits will remain.`,
            )
          ) {
            remove.mutate({ ids, kind: 'one-off' });
          }
        }}
        deleteLabel="Delete visit"
        deleting={remove.isPending}
      />
      <Paper elevation={0} sx={{ ...paperSx, overflow: 'hidden' }}>
        <Box sx={{ minHeight: 50, px: 2, display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2">Upcoming services</Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small" aria-label="Upcoming services">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {upcoming.slice(0, 30).map((serviceDate) => (
                <TableRow key={serviceDate}>
                  <TableCell>{date(serviceDate)}</TableCell>
                </TableRow>
              ))}
              {!upcoming.length && (
                <TableRow>
                  <TableCell align="center" sx={{ height: 100, color: 'text.secondary' }}>
                    No upcoming services.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body2" sx={{ px: 2, py: 1.25 }}>
          Rows: {Math.min(upcoming.length, 30)}
        </Typography>
      </Paper>
    </Stack>
  );
}

function ScheduleSubgrid({
  title,
  ariaLabel,
  records,
  selectedIds,
  onSelectionChange,
  columns,
  cells,
  onNew,
  onOpen,
  onDelete,
  deleteLabel,
  deleting,
}: {
  title: string;
  ariaLabel: string;
  records: RouteAssignment[];
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  columns: string[];
  cells: (item: RouteAssignment) => string[];
  onNew: () => void;
  onOpen: (id: number) => void;
  onDelete: (ids: number[]) => void;
  deleteLabel: string;
  deleting: boolean;
}) {
  const selected = records.filter((record) => selectedIds.has(record.id));
  const allSelected = records.length > 0 && selected.length === records.length;
  const toggle = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  return (
    <Paper elevation={0} sx={{ ...paperSx, overflow: 'hidden' }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={0.5}
        sx={{ minHeight: 50, px: 1.5 }}
      >
        <Typography variant="subtitle2" sx={{ mr: 'auto' }}>
          {title}
        </Typography>
        {selected.length === 0 ? (
          <Button startIcon={<AddIcon />} onClick={onNew} sx={commandButtonSx}>
            New
          </Button>
        ) : (
          <>
            {selected.length === 1 && (
              <Button
                startIcon={<EditOutlinedIcon />}
                onClick={() => onOpen(selected[0]!.id)}
                sx={commandButtonSx}
              >
                Edit
              </Button>
            )}
            <Button
              startIcon={<DeleteOutlineIcon />}
              disabled={deleting}
              onClick={() => onDelete(selected.map((item) => item.id))}
              sx={deleteCommandButtonSx}
            >
              {deleteLabel}
            </Button>
          </>
        )}
      </Stack>
      <Divider />
      <TableContainer>
        <Table size="small" aria-label={ariaLabel} sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={() =>
                    onSelectionChange(
                      allSelected ? new Set() : new Set(records.map((item) => item.id)),
                    )
                  }
                  inputProps={{ 'aria-label': `Select all ${title.toLowerCase()}` }}
                />
              </TableCell>
              {columns.map((column) => (
                <TableCell key={column} sx={{ fontWeight: 600 }}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record) => (
              <TableRow
                key={record.id}
                hover
                selected={selectedIds.has(record.id)}
                role="link"
                tabIndex={0}
                onClick={() => onOpen(record.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen(record.id);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: -2,
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(record.id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggle(record.id)}
                    inputProps={{ 'aria-label': `Select ${title} record ${record.id}` }}
                  />
                </TableCell>
                {cells(record).map((value, index) => (
                  <TableCell key={index}>{value}</TableCell>
                ))}
              </TableRow>
            ))}
            {!records.length && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  sx={{ height: 150, color: 'text.secondary' }}
                >
                  No records.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <Typography variant="body2" sx={{ px: 2, py: 1.25 }}>
        Rows: {records.length}
      </Typography>
    </Paper>
  );
}

const date = (value: string) => new Date(value).toLocaleDateString('en-GB');
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const tableSx = {
  minWidth: 720,
  tableLayout: 'fixed',
  '& .MuiTableCell-root': { height: 48, py: 1.1, borderBottom: '1px solid #e1dfdd' },
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
const deleteCommandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'error.main' },
};
